import {
  Box,
  Button,
  Card,
  CircularProgress, Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select, type SelectChangeEvent, Stack,
  TextField,
  Typography
} from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import React, { useEffect, useState } from 'react'
import {
  type BaseAddress,
  createLegStaffer,
  type ExecRole,
  type ExecRoleLutValues,
  type ExecutiveStaffInformation,
  createExecutiveStaffer,
  updateExecutiveStaffer,
  retireExecutiveStaffer,
  type LegRoleValues,
  retireStafferFromMember,
  updateLegStaffer,
  type UsPhone
  ,
  type MemberStaffInformation,
  createComStaffer,
  updateComStaffer,
  type CommitteeStaffInformation,
  retireStafferFromCommittee
  ,
  createLeadershipStaffer,
  type LeadershipStaffInformation,
  retireStafferFromLeadership, updateLeadershipStaffer,
  getMemberAddressForOfficeLocation,
  type MemberLevel,
  getCommitteeAddressForMajorityMinority,
  type MajorityMinorityType,
  getLeadershipAddress,
  type Building
} from 'services'
import { ExecRoleSelect, PersonMiniCard, UsAddressFields, usAddressIsValid, useGlobalMessageHandler, AlertDialog, LegRoleSelect, IconButtonWithTooltip, ResponsibilityMultiSelect, PhoneCard, EditActions } from 'components'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { buildStafferAddressFromSource, generateLegalName, generateStafferEmail, getEmptyPersonPhone, getEmptyStafferEmail, filterBuildingsForMember, filterBuildingsForCommittee, filterBuildingsForLeadership, resolveNickname } from 'helpers'
import { formatErrorFieldMessages, type HttpResponse } from 'interfaces'
import { PrefixSelect, SuffixSelect } from 'components/common'
import { UnsavedChangesDialog } from 'components/us/common/UnsavedChangesDialog'
import { useSessionContext } from 'session'
import {
  PHONE_TYPES,
  US_COMMITTEE_ROLES,
  US_LEADERSHIP_OFFICER_ROLES,
  US_LEGISLATIVE_MEMBER_ROLES,
  US_STAFFER_ROLES,
  getGenderFromPrefix
} from 'utils'

export interface Office {
  code: string
  name: string
}

export const OFFICE_LOCATION = new Map<number, Office>([
  [1, { code: 'CO', name: 'Capitol Office' }],
  [2, { code: 'DO1', name: 'District Office 1' }],
  [3, { code: 'DO2', name: 'District Office 2' }],
  [4, { code: 'DO3', name: 'District Office 3' }],
  [5, { code: 'DO4', name: 'District Office 4' }],
  [6, { code: 'DO5', name: 'District Office 5' }],
  [7, { code: 'DO6', name: 'District Office 6' }],
  [8, { code: 'DO7', name: 'District Office 7' }],
  [9, { code: 'DO8', name: 'District Office 8' }],
  [10, { code: 'DO9', name: 'District Office 9' }],
  [11, { code: 'TO', name: 'Transition Office' }],
  [12, { code: 'CA1', name: 'Campaign Office 1' }],
  [13, { code: 'CA2', name: 'Campaign Office 2' }],
  [14, { code: 'CA3', name: 'Campaign Office 3' }]
])

type StaffType = MemberStaffInformation | CommitteeStaffInformation | LeadershipStaffInformation | ExecutiveStaffInformation

export interface MemberStaffCardProps<T extends StaffType = StaffType> {
  originalPosition: T
  createCard: boolean
  legRoleLuts: LegRoleValues
  loadingLegRoleTypeLutValues: boolean
  onStaffUpdate: (updatedStaff: T) => void
  addToExistingStaffers?: (updatedStaff: T) => void
  cancelCreate?: () => void
  onRetire?: (updatedStaff: T) => void
  memberAddresses?: PersonAddress[]
  availableOfficeLocations?: number[]
  committeeAddresses?: any[]
  leadershipAddress?: any
  execPositionAddress?: PersonAddress | null
  execRoleLuts?: ExecRoleLutValues
  onDirtyChange?: (dirty: boolean) => void
}

export interface PersonPhone extends UsPhone {
  personId: number
}

export interface PersonAddress extends BaseAddress {
  personId: number
  typeId: number
  addressType?: {
    id: number
    name: string
  } | null
  phones: PersonPhone[]
}

export const MemberStaffCard = <T extends StaffType>({
  originalPosition,
  createCard,
  legRoleLuts,
  loadingLegRoleTypeLutValues,
  onStaffUpdate,
  cancelCreate = () => {},
  addToExistingStaffers = () => {},
  onRetire = () => {},
  memberAddresses = [],
  availableOfficeLocations = [],
  committeeAddresses = [],
  leadershipAddress = null,
  execPositionAddress = null,
  execRoleLuts,
  onDirtyChange
}: MemberStaffCardProps<T>): React.ReactElement => {
  const [position, setPosition] = useState<T>(originalPosition)
  const [isDirty, setIsDirty] = useState(false)
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const { openNewTab } = useSessionContext()

  const updatePosition = (updater: T | ((prev: T) => T)): void => {
    setPosition(updater)
    setIsDirty(true)
    onDirtyChange?.(true)
  }

  const resetDirty = (): void => {
    setIsDirty(false)
    onDirtyChange?.(false)
  }

  // Re-sync local state when parent refreshes the prop (e.g. after a
  // bulk-assign cascade refetches the staff list). Also closes a stale
  // Unsaved Changes dialog: the position it was guarding no longer exists.
  useEffect(() => {
    setPosition(originalPosition)
    resetDirty()
    setShowUnsavedDialog(false)
  }, [originalPosition])

  const isMemberStaff = (staff: T): staff is T & MemberStaffInformation => {
    return 'legmem' in staff
  }

  const isCommitteeStaff = (staff: T): staff is T & CommitteeStaffInformation => {
    return 'committee' in staff
  }

  const isLeadershipStaff = (staff: T): staff is T & LeadershipStaffInformation => {
    return 'leadershipOfficer' in staff
  }

  const isExecutiveStaff = (staff: T): staff is T & ExecutiveStaffInformation => {
    return 'execpos' in staff
  }

  const filterBuildingsForStaffer = (buildings: Building[]): Building[] => {
    switch (true) {
      case isMemberStaff(position): {
        const { legmem: member } = position as MemberStaffInformation
        return filterBuildingsForMember(buildings, member)
      }
      case isCommitteeStaff(position): {
        const { committee } = position as CommitteeStaffInformation
        return filterBuildingsForCommittee(buildings, committee)
      }
      case isLeadershipStaff(position): {
        const { leadershipOfficer } = position as LeadershipStaffInformation
        return filterBuildingsForLeadership(buildings, leadershipOfficer)
      }
      case isExecutiveStaff(position):
        return buildings
      default:
        return buildings
    }
  }

  const [disabled, setDisabled] = useState<boolean>(!createCard)
  const [showRetireConfirmation, setShowRetireConfirmation] = useState<boolean>(false)
  const [editingCard, setEditingCard] = useState(createCard)
  const isRetireDisabled = originalPosition.person === null
  const [expanded, setExpanded] = useState(createCard)
  const [loadingClick, setLoadingClick] = useState(false)
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  const handleExpandClick = (): void => {
    setExpanded(!expanded)
  }
  const handlerClearSelection = (): void => {
    if (createCard) {
      cancelCreate()
    }
    setPosition(originalPosition)
    resetDirty()
    setDisabled(true)
    setExpanded(false)
    setEditingCard(false)
  }

  const getRetireService = async (staff: T): Promise<HttpResponse<void>> => {
    switch (true) {
      case isMemberStaff(staff): {
        const memberStaff = staff as MemberStaffInformation
        return await retireStafferFromMember(memberStaff.legmem.id, memberStaff.id)
      }
      case isCommitteeStaff(staff): {
        const committeeStaff = staff as CommitteeStaffInformation
        return await retireStafferFromCommittee(committeeStaff.committee.id, committeeStaff.id)
      }
      case isLeadershipStaff(staff): {
        const leadershipStaff = staff as LeadershipStaffInformation
        return await retireStafferFromLeadership(leadershipStaff.leadershipOfficer.id, leadershipStaff.id)
      }
      case isExecutiveStaff(staff): {
        const executiveStaff = staff as ExecutiveStaffInformation
        return await retireExecutiveStaffer(executiveStaff.execpos.id, executiveStaff.id)
      }
      default:
        return await Promise.reject(new Error('Invalid staff type'))
    }
  }

  const getStaffService = async (staff: T): Promise<HttpResponse<T>> => {
    if (isMemberStaff(staff)) {
      return (await (staff.id === 0 ? createLegStaffer(staff) : updateLegStaffer(staff))) as HttpResponse<T>
    }
    if (isCommitteeStaff(staff)) {
      return (await (staff.id === 0 ? createComStaffer(staff) : updateComStaffer(staff))) as HttpResponse<T>
    }
    if (isLeadershipStaff(staff)) {
      return (await (staff.id === 0 ? createLeadershipStaffer(staff) : updateLeadershipStaffer(staff))) as HttpResponse<T>
    }
    if (isExecutiveStaff(staff)) {
      return (await (staff.id === 0 ? createExecutiveStaffer(staff) : updateExecutiveStaffer(staff))) as HttpResponse<T>
    }
    return await Promise.reject(new Error('Invalid staff type'))
  }

  const handleRetire = (): void => {
    clearMessages()
    setLoadingClick(true)

    getRetireService(originalPosition)
      .then(value => {
        if (value.succeeded) {
          showSuccessMessage('Staffer Retired!')
          onRetire(originalPosition)
        } else {
          showErrorMessage(formatErrorFieldMessages(value.errors))
        }
      })
      .catch((reason) => {
        showErrorMessage('Unexpected error retiring staffer.')
        console.error(reason)
      })
      .finally(() => { setLoadingClick(false) })
  }

  const handleSave = async (): Promise<boolean> => {
    clearMessages()
    if (position.address != null) {
      if (!usAddressIsValid(position.address, showErrorMessage)) return false
    }
    setLoadingClick(true)
    try {
      const positionToSave = {
        ...position,
        person: { ...position.person, nickname: resolveNickname(position.person.nickname, position.person.firstName) },
        address: position.address != null
          ? {
              ...position.address,
              phones: position.address.phones.filter(phone =>
                (phone.areaCode !== undefined && phone.areaCode !== null && phone.areaCode !== '') ||
                (phone.phoneNumber !== undefined && phone.phoneNumber !== null && phone.phoneNumber !== '') ||
                (phone.extension !== undefined && phone.extension !== null && phone.extension !== '')
              )
            }
          : position.address
      }

      const newPosition = await getStaffService(positionToSave)

      if (newPosition.result != null) {
        setPosition(newPosition.result)
        resetDirty()
        setDisabled(true)
        setExpanded(false)
        showSuccessMessage(position.id === 0 ? 'Staffer Created!' : 'Staffer Information updated')
        onStaffUpdate(newPosition.result)
        if (createCard) {
          addToExistingStaffers(newPosition.result)
          handlerClearSelection()
        }
        return true
      }
      return false
    } catch (error: any) {
      showErrorMessage(formatErrorFieldMessages(error.response.data.errors))
      return false
    } finally {
      setLoadingClick(false)
    }
  }

  const navigateToPerson = (): void => { openNewTab(`/us/editPerson/${position.person.id}`) }

  const handleNameClick = (): void => {
    if (loadingClick) return
    if (isDirty) {
      setShowUnsavedDialog(true)
    } else {
      navigateToPerson()
    }
  }

  const handleUnsavedSave = (): void => {
    setShowUnsavedDialog(false)
    void handleSave()
      .then((succeeded) => {
        if (succeeded) {
          navigateToPerson()
        } else {
          setExpanded(true)
        }
      })
      .catch((reason) => {
        showErrorMessage('Unexpected error saving staffer.')
        console.error(reason)
        setExpanded(true)
      })
  }

  const handleUnsavedDiscard = (): void => {
    setShowUnsavedDialog(false)
    navigateToPerson()
  }

  const handleUnsavedCancel = (): void => {
    setShowUnsavedDialog(false)
  }

  const handlePersonFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<number>): void => {
    const { name, value } = event.target

    updatePosition((position) => {
      const updatedPerson = { ...position.person, [name]: value }

      if (name === 'prefixId') {
        const genderId = getGenderFromPrefix(value as number)
        if (genderId !== null) {
          updatedPerson.genderId = genderId
        }
      }

      return {
        ...position,
        person: updatedPerson
      }
    })
  }

  const handleEmailValueChange = (newValue: string): void => {
    updatePosition((position) => ({
      ...position,
      email: newValue.trim() === ''
        ? null
        : position.email != null
          ? { ...position.email, value: newValue }
          : { ...getEmptyStafferEmail(position.person?.id ?? 0), value: newValue }
    }))
  }

  const handlePositionFieldChange = (key: string, newValue: number | string): void => {
    updatePosition((position) => ({
      ...position,
      [key]: newValue
    }))
  }

  const handlePositionTypeChange = (key: string, newValue: number | string): void => {
    if (newValue === '' || newValue === 0) {
      updatePosition((position) => ({
        ...position,
        [key]: null
      }))
    } else {
      const newType = legRoleLuts.legRoles.find((roleType) => roleType.id === Number(newValue))
      if (newType != null) {
        updatePosition((position) => ({
          ...position,
          [key]: newType
        }))
      }
    }
  }

  const handleGenerateLegalName = (): void => {
    updatePosition(prevPosition => ({
      ...prevPosition,
      person: { ...prevPosition.person, legalName: generateLegalName(prevPosition.person, []) }
    }))
  }

  const handleCopyEmail = (): void => {
    const email = position.email?.value
    if (email == null || email === '') return
    navigator.clipboard.writeText(email)
      .then(() => { showSuccessMessage('Email copied!') })
      .catch(() => { showErrorMessage('Unable to copy email.') })
  }

  const handleGenerateStafferEmail = (): void => {
    if (isExecutiveStaff(position)) return
    const legPosition = position as MemberStaffInformation | CommitteeStaffInformation | LeadershipStaffInformation
    const updatedEmail = generateStafferEmail({ ...legPosition })
    updatePosition(prevPosition => ({
      ...prevPosition,
      email: updatedEmail.trim() === ''
        ? null
        : prevPosition.email != null
          ? { ...prevPosition.email, value: updatedEmail }
          : { ...getEmptyStafferEmail(prevPosition.person?.id ?? 0), value: updatedEmail }
    }))
  }

  const handleUseDefaultAddressForMember = (): void => {
    if (!isMemberStaff(position) || position.legmemOfficeLocationId === 0 || position.legmemOfficeLocationId == null) return

    const memberLevel: MemberLevel = position.legmem.legrole.role.id === US_LEGISLATIVE_MEMBER_ROLES.FED_LEGISLATIVE_MEMBER ? 'federal' : 'state'
    const memberAddress = getMemberAddressForOfficeLocation(
      memberAddresses,
      position.legmemOfficeLocationId,
      memberLevel
    )

    if (memberAddress != null) {
      updatePosition(prevPosition => ({
        ...prevPosition,
        address: buildStafferAddressFromSource(prevPosition.address, memberAddress, prevPosition.person?.id ?? 0)
      }))
      showSuccessMessage('Address populated from member information')
    } else {
      showErrorMessage('No matching member address found for this location')
    }
  }

  const handleUseDefaultAddressForCommittee = (): void => {
    if (!isCommitteeStaff(position)) return

    const { majMin } = position as CommitteeStaffInformation
    const majorityMinorityType: MajorityMinorityType = majMin === 'MAJ' || majMin === 'OTH' ? 'MAJ' : 'MIN'
    const committeeAddress = getCommitteeAddressForMajorityMinority(committeeAddresses, majorityMinorityType)

    if (committeeAddress == null) {
      showErrorMessage(`No ${majorityMinorityType.toLowerCase() === 'maj' ? 'majority' : 'minority'} committee address found`)
      return
    }

    updatePosition(prev => ({
      ...prev,
      address: buildStafferAddressFromSource(prev.address, committeeAddress, prev.person?.id ?? 0)
    }))

    showSuccessMessage(`Address populated from committee ${majorityMinorityType.toLowerCase() === 'maj' ? 'majority' : 'minority'} information`)
  }

  const handleUseDefaultAddressForLeadership = (): void => {
    if (!isLeadershipStaff(position)) return

    const leadership = getLeadershipAddress(leadershipAddress)

    if (leadership == null) {
      showErrorMessage('No leadership officer address found')
      return
    }

    updatePosition(prevPosition => ({
      ...prevPosition,
      address: buildStafferAddressFromSource(prevPosition.address, leadership, prevPosition.person?.id ?? 0)
    }))
    showSuccessMessage('Address populated from leadership officer information')
  }

  const handleUseDefaultAddressForExecutive = (): void => {
    if (!isExecutiveStaff(position) || execPositionAddress == null) return

    updatePosition(prevPosition => ({
      ...prevPosition,
      address: buildStafferAddressFromSource(
        prevPosition.address,
        execPositionAddress,
        prevPosition.person?.id ?? 0,
        [PHONE_TYPES.PHONE, PHONE_TYPES.FAX]
      )
    }))
    showSuccessMessage('Address populated from position default')
  }

  const handleUseDefaultAddress = (): void => {
    if (isMemberStaff(position)) {
      handleUseDefaultAddressForMember()
      return
    }

    if (isCommitteeStaff(position)) {
      handleUseDefaultAddressForCommittee()
      return
    }

    if (isLeadershipStaff(position)) {
      handleUseDefaultAddressForLeadership()
      return
    }

    if (isExecutiveStaff(position)) {
      handleUseDefaultAddressForExecutive()
      return
    }

    showErrorMessage('Position type not supported for default address')
  }

  const isDisabledFields = (): boolean => {
    return disabled && !editingCard
  }

  const generatePersonTextFieldComponent = (xsSize: number, id: string, fieldName: string, labelValue: string, value?: string): React.ReactNode => {
    return (
      <Grid item xs={xsSize}>
        <TextField
          id={id}
          name={fieldName}
          label={labelValue}
          variant="outlined"
          disabled={disabled}
          value={value != null ? value : ''}
          onChange={handlePersonFieldChange}
          fullWidth
        />
      </Grid>
    )
  }

  const COMMITTEE_ROLE_TO_STAFFER_ROLE = new Map<number, number>([
    [US_COMMITTEE_ROLES.FED_CAUCUS, US_STAFFER_ROLES.FEDERAL_CAUCUS_STAFF],
    [US_COMMITTEE_ROLES.FED_COMMITTEE, US_STAFFER_ROLES.FEDERAL_COMMITTE_STAFF],
    [US_COMMITTEE_ROLES.FED_SUBCOMMITTEE, US_STAFFER_ROLES.FEDERAL_COMMITTE_STAFF],
    [US_COMMITTEE_ROLES.STATE_CAUCUS, US_STAFFER_ROLES.STATE_CAUCUS_STAFF],
    [US_COMMITTEE_ROLES.STATE_COMMITTEE, US_STAFFER_ROLES.STATE_COMMITTE_STAFF],
    [US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE, US_STAFFER_ROLES.STATE_COMMITTE_STAFF]
  ])

  const LEADERSHIP_ROLE_TO_STAFFER_ROLE = new Map<number, number>([
    [US_LEADERSHIP_OFFICER_ROLES.FEDERAL_LEADERSHIP, US_STAFFER_ROLES.FEDERAL_LEADERSHIP_STAFF],
    [US_LEADERSHIP_OFFICER_ROLES.FEDERAL_OFFICER, US_STAFFER_ROLES.FEDERAL_OFFICER_STAFF],
    [US_LEADERSHIP_OFFICER_ROLES.STATE_LEADERSHIP, US_STAFFER_ROLES.STATE_LEADERSHIP_STAFF],
    [US_LEADERSHIP_OFFICER_ROLES.STATE_OFFICER, US_STAFFER_ROLES.STATE_OFFICER_STAFF]
  ])

  const getPossibleStafferLegRoles = (): number[] => {
    if (isExecutiveStaff(position)) {
      return []
    }

    if (isMemberStaff(position)) {
      return position.legmem.legrole.role.id === US_LEGISLATIVE_MEMBER_ROLES.FED_LEGISLATIVE_MEMBER ? [US_STAFFER_ROLES.FEDERAL_MEMBER_STAFF] : [US_STAFFER_ROLES.STATE_MEMBER_STAFF]
    }

    if (isCommitteeStaff(position)) {
      const committeeRoleId = (position as CommitteeStaffInformation)?.committee?.role?.id
      const stafferRole = committeeRoleId !== undefined ? COMMITTEE_ROLE_TO_STAFFER_ROLE.get(committeeRoleId) : undefined
      if (stafferRole == null) {
        throw new Error(`Invalid committee staff role id: ${committeeRoleId}`)
      }
      return [stafferRole]
    }

    if (isLeadershipStaff(position)) {
      const leadershipRoleId = (position as LeadershipStaffInformation)?.leadershipOfficer?.role?.id
      const stafferRole = leadershipRoleId !== undefined ? LEADERSHIP_ROLE_TO_STAFFER_ROLE.get(leadershipRoleId) : undefined
      if (stafferRole == null) {
        throw new Error(`Invalid leadership staff role id: ${leadershipRoleId}`)
      }
      return [stafferRole]
    }

    throw new Error('Invalid staff type')
  }

  type ExecRoleField = 'execrole' | 'execrole2' | 'execrole3'
  type LegRoleField = 'legrole' | 'legrole2' | 'legrole3'

  const renderRoleSelect = (slot: 1 | 2 | 3, allowNone: boolean = false): React.ReactNode => {
    if (isExecutiveStaff(position)) {
      const field = (slot === 1 ? 'execrole' : `execrole${slot}`) as ExecRoleField
      const execPosition = position as ExecutiveStaffInformation
      return (
        <ExecRoleSelect
          label={`Executive Role #${slot}`}
          value={execPosition[field] ?? null}
          options={execRoleLuts?.execRoles?.filter(r => r.role.id === execPosition.role.id) ?? []}
          disabled={isDisabledFields()}
          onChange={(next: ExecRole | null) => {
            updatePosition((prev) => ({ ...prev, [field]: next }))
          }}
        />
      )
    }

    const field = (slot === 1 ? 'legrole' : `legrole${slot}`) as LegRoleField
    const legPosition = position as MemberStaffInformation | CommitteeStaffInformation | LeadershipStaffInformation
    return (
      <LegRoleSelect
        value={legPosition[field]?.id ?? null}
        onChange={(event) => { handlePositionTypeChange(field, event.target.value) }}
        disabled={isDisabledFields()}
        label={`Legislative Role #${slot}`}
        roleId={getPossibleStafferLegRoles()}
        allowNone={allowNone}
      />
    )
  }

  return (
    <Card key={`staff-card-${(position.person.id != null) ? position.person.id : ''}`} sx={{ width: '98%', padding: 2, border: expanded ? '1px solid' : 'none', borderColor: expanded ? 'primary.main' : 'transparent' }}>
      <Grid container alignItems={'center'} spacing={2}>
        { expanded && (<Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={1}>
              <PrefixSelect
                value={position.person.prefixId ?? null}
                onChange={handlePersonFieldChange}
                disabled={disabled}
              />
            </Grid>
            {generatePersonTextFieldComponent(3.5, 'first_name', 'firstName', 'First Name', position.person.firstName)}
            {generatePersonTextFieldComponent(3, 'middle_name', 'middleName', 'Middle Name', position.person.middleName)}
            {generatePersonTextFieldComponent(3.5, 'last_name', 'lastName', 'Last Name', position.person.lastName)}

            <Grid item xs={1}>
              <SuffixSelect
                value={position.person.suffixId ?? null}
                onChange={handlePersonFieldChange}
                disabled={disabled}
              />
            </Grid>
            {generatePersonTextFieldComponent(2, 'prof_suffix', 'profSuffix', 'Professional Suffix', position.person.profSuffix)}
            <Grid item xs={3}>
              <TextField
                id="legal_name"
                name="legalName"
                label="Legal Name"
                variant="outlined"
                disabled={disabled}
                value={position.person.legalName != null ? position.person.legalName : ''}
                onChange={handlePersonFieldChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButtonWithTooltip
                        label="Generate Legal Name"
                        color="primary"
                        disabled={disabled}
                        onClick={handleGenerateLegalName}
                        icon={<AutoFixHighIcon/>}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            {generatePersonTextFieldComponent(3, 'nickname', 'nickname', 'Nickname', position.person.nickname)}
            {generatePersonTextFieldComponent(2, 'pronunciation', 'pronunciation', 'Pronunciation', position.person.pronunciation)}
        </Grid>
            <Divider sx={{ pb: 1, pt: 1, borderColor: 'primary.main' }} />        </Grid>
        )}
          <Grid item xs={'auto'}>
            <IconButtonWithTooltip
              label={expanded ? 'Collapse' : 'Expand'}
              color="primary"
              onClick={handleExpandClick}
              icon={expanded ? <RemoveIcon /> : <AddIcon />}
            />
          </Grid>
        <Grid item xs>
          <FormControl fullWidth>
            <TextField
              name={'email'}
              disabled={disabled}
              label={'Email'}
              value={position.email?.value}
              onChange={(event) => { handleEmailValueChange(event.target.value) }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButtonWithTooltip
                      label="Copy Email"
                      color="primary"
                      disabled={position.email?.value == null || position.email.value === ''}
                      onClick={handleCopyEmail}
                      icon={<ContentCopyIcon/>}
                    />
                    <IconButtonWithTooltip
                      label="Generate Staffer Email"
                      color="primary"
                      disabled={disabled}
                      onClick={handleGenerateStafferEmail}
                      icon={<AutoFixHighIcon/>}
                    />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
        </Grid>
          <Grid item xs={2.5}>
            <FormControl fullWidth>
              <TextField name={'title'} disabled={disabled} label={'Title'} value={position.title} onChange={(event) => { handlePositionFieldChange('title', event.target.value) }}/>
            </FormControl>
          </Grid>
          <Grid item xs={2.25}>
            {renderRoleSelect(1)}
          </Grid>
        {isMemberStaff(position) &&
          <Grid item xs={!expanded ? 0.75 : 1}>
            <FormControl fullWidth>
              <InputLabel id={`selectLocation-${position.id}`}>Location</InputLabel>
              <Select
                disabled={isDisabledFields()}
                onChange={(event) => { handlePositionFieldChange('legmemOfficeLocationId', Number(event.target.value)) }}
                value={position.legmemOfficeLocationId}
                labelId={`selectStaffLocation-${position.id}`}
                label={'Location'}
              >
                {
                  Array.from(OFFICE_LOCATION.entries())
                    .filter(([key]) => availableOfficeLocations.length === 0 || availableOfficeLocations.includes(key))
                    .map(([key, office]) => (
                      <MenuItem key={key} value={key}>
                        {office.code}
                      </MenuItem>
                    ))
                }
              </Select>
            </FormControl>
          </Grid>
        }
          <Grid item xs={expanded ? 1.5 : 2.5}>
            {!expanded && (<PersonMiniCard person={position.person} filled onNameClick={handleNameClick}/>)}
          </Grid>
        {loadingClick && <CircularProgress sx={{ ml: 3 }}/>}
        {!loadingClick &&
          <Grid item xs={'auto'}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
              <EditActions
                entityName="Staffer"
                editing={!disabled}
                onEdit={() => {
                  setDisabled(false)
                  setExpanded(true)
                }}
                onCancel={() => { handlerClearSelection() }}
                onSave={() => { void handleSave() }}
              />
              {disabled && (
                <IconButtonWithTooltip
                  label="Retire Staffer"
                  color="secondary"
                  disabled={isRetireDisabled}
                  onClick={() => { setShowRetireConfirmation(true) }}
                  icon={<HourglassEmptyIcon/>}
                />
              )}
            </Box>
          </Grid>
          }

        { expanded &&
          <Grid item xs={12}>
            <Divider sx={{ pb: 1, pt: 1, borderColor: 'primary.main' }} />
          </Grid>
        }
        { expanded && <Grid item xs={12}>
            {(position.address !== null) &&
              <Box>
              {((isMemberStaff(position) && (position as MemberStaffInformation).legmemOfficeLocationId !== 0 && (position as MemberStaffInformation).legmemOfficeLocationId != null && memberAddresses.length > 0) ||
                (isCommitteeStaff(position) && committeeAddresses.length > 0) ||
                (isLeadershipStaff(position) && leadershipAddress != null) ||
                (isExecutiveStaff(position) && execPositionAddress != null)) && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isDisabledFields()}
                    onClick={handleUseDefaultAddress}
                  >
                    Use Default
                  </Button>
                </Box>
              )}
              <UsAddressFields
                disabled={isDisabledFields()}
                startExpanded={editingCard}
                updateAddress={(baseAddress: BaseAddress) => {
                  updatePosition(prev => ({
                    ...prev,
                    address: {
                      ...prev.address,
                      ...baseAddress,
                      personId: position.person?.id ?? 0,
                      phones: position.address?.phones ?? [],
                      typeId: baseAddress.typeId ?? 0
                    }
                  }))
                }}
                resetAddress={() => { } }
                comAddress={position.address}
                showPhones={false}
                filterBuildings={filterBuildingsForStaffer}
            />
                <Box pt={2}>
                  {position.address.phones.length > 0 && (
                    <>
                      <Typography variant="body2" color={'secondary.dark'} sx={{ fontWeight: 'bold' }} paddingBottom={2}>Phones:</Typography>
                      <Stack spacing={1}>
                        {position.address.phones.map((phone, index) => (
                          <PhoneCard
                            key={phone.id}
                            phone={phone}
                            disabled={isDisabledFields()}
                            onDeletePhone={(phoneToDelete) => {
                              updatePosition(prev => ({
                                ...prev,
                                address: {
                                  ...prev.address,
                                  phones: prev.address.phones.filter(p => p.id !== phoneToDelete.id)
                                }
                              }))
                            }}
                            onUpdatePhone={(updatedPhone) => {
                              updatePosition(prev => ({
                                ...prev,
                                address: {
                                  ...prev.address,
                                  phones: prev.address.phones.map(p => p.id === updatedPhone.id ? updatedPhone : p)
                                }
                              }))
                            }}
                            address={position.address}
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <IconButtonWithTooltip
                      label="Add Phone"
                      color="secondary"
                      disabled={isDisabledFields()}
                      onClick={() => {
                        updatePosition(prev => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            phones: [...prev.address.phones, getEmptyPersonPhone(position.person?.id ?? 0, position.address?.id ?? 0)]
                          }
                        }))
                      } }
                      icon={<AddIcon />}
                    />
                  </Box>
                </Box>
              </Box>}
            {(position.address == null) && <Typography marginLeft={1} variant={'caption'}>The position has no address.</Typography>}
          </Grid>}

        { expanded &&
          <>
            <Grid item xs={12}>
              <Divider sx={{ pb: 1, pt: 1, borderColor: 'primary.main' }} />            </Grid>

          <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Stack spacing={2}>

                    {renderRoleSelect(2, true)}

                    {renderRoleSelect(3, true)}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <ResponsibilityMultiSelect
                    disabled={isDisabledFields()}
                    value={position.responsibilities}
                    onChange={(selectedIds) => {
                      updatePosition(prev => ({ ...prev, responsibilities: selectedIds }))
                    }}
                  />
                </Grid>
              </Grid>
        </Grid>
          </>

        }
      </Grid>
      <AlertDialog
        onClose={() => { setShowRetireConfirmation(false) }}
        open={showRetireConfirmation}
        onButtonClick={handleRetire}
        title='Would you like to retire this staffer?'
        contentText='Are you sure you want to retire this staffer? If you confirm, please click on "Confirm."'
        nameButtonCancel='Close'
        nameButtonConfirm='Confirm'
      />
      {showUnsavedDialog && (
        <UnsavedChangesDialog
          open={showUnsavedDialog}
          onSave={handleUnsavedSave}
          onDiscard={handleUnsavedDiscard}
          onCancel={handleUnsavedCancel}
        />
      )}
    </Card>
  )
}
